import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { UseGetStoreData } from "../getStoreData/UseGetStoreData";
import { createExtendedTrip, deleteDropLocation, roadDistanceAndTime, addDeltaCharges } from "../../http";
import { useAmbulanceBooking } from "../useAmbulanceBooking/UseAmbulanceBooking";
import { setCharges, setDropLocations } from "../../redux/extraChargesSlice/extraChargesSlice";

export const useExtraCharges = () => {
  const { getTicketData } = UseGetStoreData();
  const { ambulanceData } = useAmbulanceBooking();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const extraCharges = useSelector((state: any) => state.extraCharges.charges);
  const dropLocations = useSelector((state: any) => state.extraCharges.dropLocations);

  const [isSaving, setIsSaving] = useState(false);
  const [savedDropCount, setSavedDropCount] = useState(0);
  const [distanceData, setDistanceData] = useState<any[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (ambulanceData?.[0]?.dropLocation && ambulanceData[0].dropLocation.length > 0) {
      if (dropLocations.length === 0) {
        dispatch(setDropLocations(ambulanceData[0].dropLocation));
      }
      setSavedDropCount(ambulanceData[0].dropLocation.length);
    }
    
    // Load delta charges and assistance required from ambulanceData
    if (ambulanceData?.[0]) {
      const data = ambulanceData[0];
      const charges: any = {};
      
      if (data.deltaCharges) {
        if (data.deltaCharges.oxygenCharge) charges.oxygenCharge = String(data.deltaCharges.oxygenCharge);
        if (data.deltaCharges.waitingCharge) charges.waitingCharge = String(data.deltaCharges.waitingCharge);
        if (data.deltaCharges.ambulanceCharge) charges.ambulanceCharge = String(data.deltaCharges.ambulanceCharge);
        if (data.deltaCharges.helper?.numberOfHelper) charges.helper = data.deltaCharges.helper.numberOfHelper;
        if (data.deltaCharges.other) charges.others = String(data.deltaCharges.other);
      }
      
      // if (data.typeOfAssistanceRequired) {
      //   charges.wheelchair = data.typeOfAssistanceRequired.wheelChair || false;
      //   charges.stretcher = data.typeOfAssistanceRequired.stretcher || false;
      //   charges.paramedic = data.typeOfAssistanceRequired.paramedic || false;
      //   charges.doctorOnBoard = data.typeOfAssistanceRequired.DoctorOnBoard || false;
      // }
      
      if (Object.keys(charges).length > 0) {
        dispatch(setCharges(charges));
      }
    }
  }, [ambulanceData, dropLocations.length, dispatch]);

  useEffect(() => {
    const calculateDistances = async () => {
      if (dropLocations.length < 2) {
        setDistanceData([]);
        return;
      }

      const distances = [];
      for (let i = 0; i < dropLocations.length - 1; i++) {
        const origin = dropLocations[i];
        const destination = dropLocations[i + 1];

        if (origin?.location?.coordinates && destination?.location?.coordinates) {
          try {
            const payload = {
              targetLat: origin.location.coordinates[0],
              targetLng: origin.location.coordinates[1],
              address: [{ coordinates: [destination.location.coordinates[1], destination.location.coordinates[0]] }]
            };
            const res = await roadDistanceAndTime(payload);
            distances.push({
              distance: res.data.data[0]?.distance?.text || 'N/A',
              duration: res.data.data[0]?.duration_in_traffic?.text || 'N/A'
            });
          } catch (error) {
            distances.push({ distance: 'N/A', duration: 'N/A' });
          }
        } else {
          distances.push({ distance: 'N/A', duration: 'N/A' });
        }
      }
      setDistanceData(distances);
    };

    calculateDistances();
  }, [dropLocations]);

  const handleSetExtraCharges = (charges: any) => {
    dispatch(setCharges(charges));
    setHasUnsavedChanges(true);
  };

  const handleSetDropLocations = (locations: any[]) => {
    dispatch(setDropLocations(locations));
    setHasUnsavedChanges(true);
  };

  const handleDeleteDropLocation = async (index: number) => {
    const isExistingInDB = index < savedDropCount;

    if (!isExistingInDB) {
      const newLocations = dropLocations.filter((_: any, i: number) => i !== index);
      dispatch(setDropLocations(newLocations));
      return;
    }

    try {
      const ticketId = getTicketData?.ticketId || getTicketData?._id;
      await deleteDropLocation(String(ticketId), index);
      const newLocations = dropLocations.filter((_: any, i: number) => i !== index);
      dispatch(setDropLocations(newLocations));
      setSavedDropCount(prev => prev - 1);
      alert('Drop location deleted successfully');
    } catch (error: any) {
      console.error('Error deleting drop location:', error);
      const errorMessage = error?.response?.data?.message;
      alert(errorMessage || 'Failed to delete drop location');
    }
  };

  const handleSelectDropLocation = (index: number) => {
    sessionStorage.setItem('extraChargeEditingIndex', index.toString());
    navigate('/logistics', { state: { from: 'extraCharges-Drop Location' } });
  };

  const saveDropLocations = async (locations: any[]) => {
    setIsSaving(true);
    try {
      const ticketId = getTicketData?.ticketId || getTicketData?._id;
      
      const newDropsOnly = locations.slice(savedDropCount);
      const validLocations = newDropsOnly
        .filter(loc => loc !== null && loc !== undefined)
        .map(loc => ({
          hospitalName: loc?.hospitalName || loc?.name,
          address: loc?.address,
          label: loc?.label,
          city: loc?.city,
          location: loc?.location
        }));

      if (validLocations.length > 0) {
        await createExtendedTrip({
          ticket_id: String(ticketId),
          dropLocation: validLocations
        });
      }
      
      const deltaCharges: any = {};
      if (extraCharges.oxygenCharge) deltaCharges.oxygenCharge = Number(extraCharges.oxygenCharge);
      if (extraCharges.waitingCharge) deltaCharges.waitingCharge = Number(extraCharges.waitingCharge);
      if (extraCharges.ambulanceCharge) deltaCharges.ambulanceCharge = Number(extraCharges.ambulanceCharge);
      if (extraCharges.helper !== undefined && extraCharges.helper !== null) deltaCharges.helper = Number(extraCharges.helper);
      if (extraCharges.others) deltaCharges.other = Number(extraCharges.others);
      // if (extraCharges.wheelchair) deltaCharges.wheelChair = extraCharges.wheelchair;
      // if (extraCharges.stretcher) deltaCharges.stretcher = extraCharges.stretcher;
      // if (extraCharges.paramedic) deltaCharges.paramedic = extraCharges.paramedic;
      // if (extraCharges.doctorOnBoard) deltaCharges.DoctorOnBoard = extraCharges.doctorOnBoard;
      
        if (Object.keys(deltaCharges).length > 0) {
          await addDeltaCharges({
            ticket_id: String(ticketId),
            deltaCharges,
            ambulanceProvider: ambulanceData?.[0]?.ambulanceProvider
          });
        }

      
      setHasUnsavedChanges(false);
      alert('Saved successfully');
      window.location.reload();
    } catch (error: any) {
      console.error('Error saving:', error);
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        alert(errorMessage.join(', '));
      } else {
        alert(errorMessage || 'Failed to save');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return {
    extraCharges,
    setExtraCharges: handleSetExtraCharges,
    dropLocations,
    setDropLocations: handleSetDropLocations,
    handleSelectDropLocation,
    handleDeleteDropLocation,
    saveDropLocations,
    isSaving,
    distanceData,
    ambulanceData,
    hasUnsavedChanges
  };
};
